/**
 * The figure ledger, laid out as the spreadsheet the analyst would otherwise
 * be keeping by hand: numbered rows, lettered columns, the prior period beside
 * the current one, and the source of every number in the last column.
 *
 * The shape is deliberate. An analyst checking a covenant package reads across
 * a row — last quarter, this quarter, what moved, where it came from — and the
 * grid is the artifact they already trust. Every cell states its provenance
 * (`LedgerCell`), so nothing here needs to be taken on faith.
 */

import React from "react";

import type {
	MonitoringFigure,
	MonitoringFigureGroup,
	MonitoringRun,
} from "../../types/monitoring";
import LedgerCell from "./LedgerCell";
import { NO_VALUE, formatDelta, formatFigure } from "./runFormat";
import MonitoringLegend from "./MonitoringLegend";

export interface RunLedgerGridProps {
	run: MonitoringRun;
	onOpenSource: (figure: MonitoringFigure) => void;
	onOpenGaps: (figure: MonitoringFigure) => void;
}

/** Column B's treatments, matching `LedgerCell`'s so a row reads as one row. */
const CITED_UNDERLINE = "border-b-[1.5px] border-[#004C4B]/35";
const UNVERIFIED_UNDERLINE =
	"underline decoration-dotted decoration-warning-fg underline-offset-4";

/** Why column B is an em dash, said in words for the hover. */
function priorGapReason(figure: MonitoringFigure): string {
	const prior = figure.prior;
	if (!prior) return "No prior period value for this figure";
	switch (prior.state) {
		case "gap":
			return "Not in the prior period's package";
		case "not_available":
			return "Not available for the prior period";
		default:
			return "No prior period value for this figure";
	}
}

interface PriorCellProps {
	figure: MonitoringFigure;
	onOpenSource: (figure: MonitoringFigure) => void;
}

/**
 * Column B: the same figure, one period earlier (DOP-465).
 *
 * It reads off `figure.prior` — the run's own answer for last period — rather
 * than off a compare of two locked runs, so the column is populated on the
 * first run of a template and does not wait on anybody locking anything. The
 * provenance treatments are `LedgerCell`'s: a cited prior is clickable to its
 * page, a derived one keeps its Σ, and a number that does not exist is an em
 * dash that says why on hover rather than a silent blank.
 */
const PriorCell: React.FC<PriorCellProps> = ({ figure, onOpenSource }) => {
	const prior = figure.prior ?? null;
	const cellClass =
		"border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-text-muted";
	const testId = `ledger-prior-${figure.key}`;

	if (
		!prior ||
		prior.value === null ||
		prior.state === "gap" ||
		prior.state === "not_available"
	) {
		return (
			<td className={cellClass} data-testid={testId} title={priorGapReason(figure)}>
				<span className="text-text-subtle">{NO_VALUE}</span>
			</td>
		);
	}

	const text = formatFigure(prior.value, figure.unit);

	if (prior.state === "derived") {
		return (
			<td className={cellClass} data-testid={testId}>
				{prior.verified ? (
					text
				) : (
					<span
						className={UNVERIFIED_UNDERLINE}
						title="Does not tie to the prior certificate"
					>
						{text}
					</span>
				)}
				<span
					className="ml-1 text-gray-400"
					title="Computed from other figures"
					data-testid={`ledger-prior-derived-${figure.key}`}
				>
					Σ
				</span>
			</td>
		);
	}

	// found (and anything else carrying a number): cite it where we can.
	const source = prior.source;
	if (prior.state === "found" && source?.anchor) {
		return (
			<td className={cellClass} data-testid={testId}>
				<button
					type="button"
					data-testid={`ledger-prior-value-${figure.key}`}
					// The page's opener reads `figure.source`, so the prior's source
					// is handed over on a copy of the figure. One affordance, one
					// viewer — the prior number opens its page like the current one.
					onClick={() => onOpenSource({ ...figure, source })}
					title={
						prior.verified
							? `${source.filename}${source.page ? ` · p.${source.page}` : ""}`
							: "Quote not re-read on the page"
					}
					className={`tabular-nums ${
						prior.verified ? CITED_UNDERLINE : UNVERIFIED_UNDERLINE
					}`}
				>
					{text}
				</button>
			</td>
		);
	}

	return (
		<td className={cellClass} data-testid={testId}>
			<span
				title={prior.verified ? undefined : "Quote not re-read on the page"}
				className={prior.verified ? undefined : UNVERIFIED_UNDERLINE}
			>
				{text}
			</span>
		</td>
	);
};

/** Group order and headings, matching the export's Summary sheet. */
const GROUPS: Array<{ key: MonitoringFigureGroup; label: string }> = [
	{ key: "financials", label: "Financials ($M, LTM unless stated)" },
	{ key: "leverage", label: "Leverage & coverage" },
	{ key: "covenants", label: "Covenants" },
	{ key: "adjustments", label: "Adjustments" },
];

const HEAD_CELL =
	"border-b border-gray-200 bg-[#fafafa] px-3 py-2.5 text-xs font-semibold text-[#5B6472]";

/** What the Source column says for a figure, given its state. */
function sourceText(figure: MonitoringFigure): string {
	switch (figure.state) {
		case "found": {
			if (!figure.source) return NO_VALUE;
			const page = figure.source.page ? ` · p.${figure.source.page}` : "";
			return `${figure.source.filename}${page}`;
		}
		case "derived":
			// Nothing (DOP-460). A derived figure has no source to cite, and the
			// formula it used to print here now lives behind the cell's Σ, where
			// it can be written in labels beside the values the run used.
			return "";
		case "manual":
			return `Entered by ${figure.entered_by_name ?? "a person"}${
				figure.note ? ` · ${figure.note}` : ""
			}`;
		case "gap":
			return figure.gap_reason ?? "Not in package";
		case "not_available":
			return figure.note ?? "Not available";
		default:
			return NO_VALUE;
	}
}

const RunLedgerGrid: React.FC<RunLedgerGridProps> = ({
	run,
	onOpenSource,
	onOpenGaps,
}) => {
	// A derived cell's formula popover names its inputs by label and shows the
	// value THIS run used, so every cell needs the whole run to hand.
	const figuresByKey = new Map(
		run.figures.map((figure) => [figure.key, figure]),
	);
	// Only the NAME of the prior period comes off the run header now; the values
	// ride on each figure (DOP-465), so the column no longer depends on a
	// compare of two locked runs.
	const priorLabel = run.prior?.period.label ?? "Prior period";
	const figuresByGroup = GROUPS.map((group) => ({
		...group,
		figures: run.figures
			.filter((figure) => figure.group === group.key)
			.sort((a, b) => a.sort_order - b.sort_order),
	})).filter((group) => group.figures.length > 0);

	// Excel-like: every row on screen carries a number, group headings included.
	let rowNumber = 0;

	return (
		<div className="flex min-h-0 flex-col">
			<div className="min-h-0 w-full min-w-0 flex-1 overflow-auto rounded-xl border border-[#e7e7e4] bg-white">
				<table className="w-full border-collapse text-base">
					<thead className="sticky top-0 z-20 bg-white">
						<tr>
							<th className={`${HEAD_CELL} w-8 text-right text-gray-400`}>
								{""}
							</th>
							<th scope="col" className={`${HEAD_CELL} text-left`}>
								Figure
							</th>
							<th scope="col" className={`${HEAD_CELL} text-right`}>
								{priorLabel}
							</th>
							<th scope="col" className={`${HEAD_CELL} text-right`}>
								{run.period.label}
							</th>
							<th scope="col" className={`${HEAD_CELL} text-right`}>
								Change
							</th>
							<th scope="col" className={`${HEAD_CELL} text-left`}>
								Source
							</th>
						</tr>
					</thead>
					<tbody>
						{figuresByGroup.map((group) => {
							rowNumber += 1;
							const groupRow = rowNumber;
							return (
								<React.Fragment key={group.key}>
									<tr className="bg-[#fcfcfb] text-[11px] font-bold uppercase tracking-[0.06em] text-[#004C4B]">
										<td className="border-b border-gray-200 px-3 py-1.5 text-right text-sm text-gray-400">
											{groupRow}
										</td>
										<th
											scope="colgroup"
											colSpan={5}
											className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-base font-medium text-text-strong"
										>
											{group.label}
										</th>
									</tr>
									{group.figures.map((figure) => {
										rowNumber += 1;
										const priorValue = figure.prior?.value ?? null;
										return (
											<tr
												key={figure.key}
												data-testid={`ledger-row-${figure.key}`}
												className="hover:bg-gray-50"
											>
												<td className="border-b border-gray-100 px-3 py-1.5 text-right text-sm text-gray-400">
													{rowNumber}
												</td>
												<th
													scope="row"
													className="border-b border-gray-100 px-3 py-1.5 text-left text-base font-normal text-text-base"
												>
													{figure.label}
												</th>
												<PriorCell
													figure={figure}
													onOpenSource={onOpenSource}
												/>
												<LedgerCell
													figure={figure}
													onOpenSource={onOpenSource}
													onOpenGaps={onOpenGaps}
													figuresByKey={figuresByKey}
												/>
												<td
													className="border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-text-muted"
													data-testid={`ledger-delta-${figure.key}`}
												>
													{formatDelta(priorValue, figure.value, figure.unit)}
												</td>
												<td
													className="border-b border-gray-100 px-3 py-1.5 text-left text-sm text-text-muted"
													data-testid={`ledger-source-${figure.key}`}
												>
													{sourceText(figure)}
												</td>
											</tr>
										);
									})}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* The cell states are explained on demand (DOP-472), the way the KPI
			    workflow does it, rather than by a bar of samples under the grid. */}
			<div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-text-muted">
				<MonitoringLegend />
				<span>
					{run.sources.length} source{run.sources.length === 1 ? "" : "s"} ·{" "}
					{run.package.length} document{run.package.length === 1 ? "" : "s"}
				</span>
			</div>
		</div>
	);
};

export default RunLedgerGrid;
